import React, { useState, useMemo, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { SubjectGroup, PrintSubject } from './subjectGroupTypes';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';

// ─────────────────────────────────────────────────────────────────────────────
// SubjectCombinerModal — merge N subjects into one printed column
// (e.g. Political Science + Civics + Geography → "SST")
// ─────────────────────────────────────────────────────────────────────────────

interface SubjectCombinerModalProps {
  visible: boolean;
  onHide: () => void;
  subjects: PrintSubject[];
  groups: SubjectGroup[];
  onChange: (groups: SubjectGroup[]) => void;
}

const SubjectCombinerModal: React.FC<SubjectCombinerModalProps> = ({
  visible,
  onHide,
  subjects,
  groups,
  onChange,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftIds, setDraftIds] = useState<Set<number>>(new Set());
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.combiner-modal-wrapper .p-dialog') as HTMLElement | null;
      const mask   = document.querySelector('.combiner-modal-wrapper')           as HTMLElement | null;
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask)   mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const groupedSubjectIds = useMemo(
    () => new Set(groups.filter((g) => g.id !== editingId).flatMap((g) => g.subjectIds)),
    [groups, editingId],
  );

  const startNew = () => {
    setEditingId(`grp_${Date.now()}`);
    setDraftName('');
    setDraftIds(new Set());
  };

  const startEdit = (g: SubjectGroup) => {
    setEditingId(g.id);
    setDraftName(g.name);
    setDraftIds(new Set(g.subjectIds));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraftName('');
    setDraftIds(new Set());
  };

  const toggleDraftSubject = (subjectId: number) => {
    setDraftIds((prev) => {
      const next = new Set(prev);
      next.has(subjectId) ? next.delete(subjectId) : next.add(subjectId);
      return next;
    });
  };

  const saveDraft = () => {
    const name = draftName.trim();
    if (!name) {
      window.alert('Give the combined subject a name, e.g. SST.');
      return;
    }
    if (draftIds.size < 2) {
      window.alert('Select 2 or more subjects to combine.');
      return;
    }
    const newGroup: SubjectGroup = { id: editingId!, name, subjectIds: Array.from(draftIds) };
    const exists = groups.some((g) => g.id === editingId);
    const next = exists ? groups.map((g) => (g.id === editingId ? newGroup : g)) : [...groups, newGroup];
    onChange(next);
    cancelEdit();
  };

  const removeGroup = (id: string) => {
    if (!window.confirm("Remove this combination? This won't delete any marks — just the saved grouping.")) return;
    onChange(groups.filter((g) => g.id !== id));
  };

  const subjectName = (id: number) => subjects.find((s) => s.subject_id === id)?.subject_name ?? `#${id}`;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => { cancelEdit(); onHide(); setIsClosing(false); }, 380);
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '4px 0' }}>
      <div style={{
        width: 48, height: 48, borderRadius: 13,
        background: 'linear-gradient(135deg,#1e3a8a,#3b82f6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, boxShadow: '0 6px 18px rgba(59,130,246,.25)',
        border: '2.5px solid white',
      }}>
        <i className="pi pi-sitemap" style={{ fontSize: 20, color: 'white' }} />
      </div>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1e40af' }}>
          Combine Subjects
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
          Merge subjects into one printed column, e.g. "SST"
        </div>
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible && !isClosing}
      onHide={handleClose}
      header={headerTemplate}
      style={{ width: '94vw', maxWidth: 560, borderRadius: 22, overflow: 'hidden' }}
      contentStyle={{
        padding: '18px 24px 8px',
        background: 'linear-gradient(180deg,#f8fafc,#f1f5f9)',
      }}
      headerStyle={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '18px 24px',
        borderRadius: '22px 22px 0 0',
      }}
      modal
      draggable={false}
      resizable={false}
      blockScroll
      dismissableMask
      maskClassName="combiner-modal-wrapper"
    >
      <style>{`
        .combiner-modal-wrapper { backdrop-filter:blur(4px); background-color:rgba(0,0,0,.22)!important; animation:cmFadeIn .3s ease; }
        .combiner-modal-wrapper.mask-closing { animation:cmFadeOut .38s ease!important; }
        @keyframes cmFadeIn { from{opacity:0} to{opacity:1} }
        @keyframes cmFadeOut { from{opacity:1} to{opacity:0} }
        .combiner-modal-wrapper .p-dialog { animation:cmSlideIn .3s cubic-bezier(.4,0,.2,1); box-shadow:0 20px 48px -8px rgba(0,0,0,.22)!important; }
        .combiner-modal-wrapper .p-dialog.dialog-closing { animation:cmSlideOut .38s cubic-bezier(.4,0,.2,1)!important; }
        @keyframes cmSlideIn { from{opacity:0;transform:scale(.93) translateY(-16px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes cmSlideOut { from{opacity:1;transform:scale(1) translateY(0)} to{opacity:0;transform:scale(.9) translateY(18px)} }
        .group-row { display:flex; align-items:center; gap:9px; padding:10px 13px; border-radius:11px; border:1.5px solid #e5e7eb; background:white; margin-bottom:8px; transition:all .15s; }
        .group-row:hover { border-color:#bfdbfe; background:#eff6ff; }
        .subject-chip { display:flex; align-items:center; gap:9px; padding:9px 12px; border-radius:11px; cursor:pointer; border:1.5px solid #e5e7eb; background:white; transition:all .15s; user-select:none; margin-bottom:6px; }
        .subject-chip:hover:not(.disabled) { border-color:#bfdbfe; background:#eff6ff; }
        .subject-chip.selected { border-color:#3b82f6; background:#eff6ff; }
        .subject-chip.selected .chip-check { background:#3b82f6; border-color:#3b82f6; }
        .subject-chip.selected .chip-check::after { opacity:1; }
        .subject-chip.disabled { cursor:not-allowed; opacity:.45; }
        .chip-check { width:16px; height:16px; border-radius:5px; border:1.5px solid #d1d5db; background:white; flex-shrink:0; display:flex; align-items:center; justify-content:center; transition:all .12s; }
        .chip-check::after { content:''; display:block; width:8px; height:5px; border-left:2px solid white; border-bottom:2px solid white; transform:rotate(-45deg) translateY(-1px); opacity:0; transition:opacity .1s; }
      `}</style>

      <p style={{ fontSize: 13, color: '#64748b', marginTop: 0, marginBottom: 16 }}>
        Merge subjects (e.g. Political Science + Civics + Geography) into one printed column (e.g. "SST").
      </p>

      {groups.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {groups.map((g) => (
            <div key={g.id} className="group-row">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1e40af' }}>{g.name}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {g.subjectIds.map(subjectName).join(' + ')}
                </div>
              </div>
              <Button icon="pi pi-pencil" text rounded onClick={() => startEdit(g)} />
              <Button icon="pi pi-trash" text rounded severity="danger" onClick={() => removeGroup(g.id)} />
            </div>
          ))}
        </div>
      )}

      {editingId ? (
        <div style={{ background: 'white', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: 14, marginBottom: 18 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#1e40af', marginBottom: 6 }}>
            Combined Subject Name
          </label>
          <InputText
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder="e.g. SST"
            style={{ width: '100%', marginBottom: 14, borderRadius: 10 }}
          />

          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 8, letterSpacing: '.04em' }}>
            SELECT SUBJECTS TO MERGE
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {subjects.map((sub) => {
              const disabled = groupedSubjectIds.has(sub.subject_id);
              const selected = draftIds.has(sub.subject_id);
              return (
                <div
                  key={sub.subject_id}
                  className={`subject-chip${selected ? ' selected' : ''}${disabled ? ' disabled' : ''}`}
                  onClick={() => !disabled && toggleDraftSubject(sub.subject_id)}
                >
                  <Checkbox checked={selected} disabled={disabled} onChange={() => toggleDraftSubject(sub.subject_id)} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: selected ? '#1e40af' : '#374151', flex: 1 }}>{sub.subject_name}</span>
                  {disabled && <span style={{ fontSize: 11, color: '#9ca3af' }}>in another group</span>}
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <div style={{ flex: 1 }}><GlobalButton label="Cancel" variant="outline" onClick={cancelEdit} fullWidth /></div>
            <div style={{ flex: 1 }}><GlobalButton label="Save Combination" icon="pi pi-check" onClick={saveDraft} fullWidth /></div>
          </div>
        </div>
      ) : (
        <GlobalButton
          label="Combine Subjects"
          icon="pi pi-plus-circle"
          variant="outline"
          fullWidth
          onClick={startNew}
        />
      )}
    </Dialog>
  );
};

export default SubjectCombinerModal;